export const useCases = [
  {
    id: 'amazon-seller-intelligence-action-agent',
    title: 'Amazon Seller Intelligence & Action Agent',
    route: '/amazon-seller-intelligence-action-agent',
    description: 'Turn Amazon seller data into intelligent, automated action, with agents that connect seller performance to the rest of your enterprise data, recommend what to do next, and execute approved actions.',
    category: 'Marketplace & E-commerce',
    icon: 'chat',
    about: 'Connects Amazon seller data with enterprise sales, finance, marketing, and supply chain data in Amazon Quick, so specialized agents can monitor inventory, pricing, listings, and performance and move from insight to approved action.',
    builtWith: ['Amazon Quick', 'Amazon Selling Partner plugin'],

    // Same gradient hero band as the other one-pager use cases (see
    // UseCaseView).
    heroBanner: true,

    components: {
      // Static hero image (Seller Assistant workflow screen on a laptop)
      // until a recorded walkthrough exists -- add `video: { url }` here
      // later and it takes priority over heroImage automatically (see
      // UseCaseView). Same image is used at every screen size.
      heroImage: {
        src: '/images/amazon-seller-intelligence-hero.jpg',
        alt: 'A person at a laptop on the "Create a Seller Assistant workflow" screen, with suggested workflows like Pricing Analyzer and Account Health Monitor',
      },

      // Rebuilt as responsive HTML from the use case brief, same pattern
      // as PredictiveRestockOnePager / PersonalizedContentDiscoveryOnePager.
      richContent: 'AmazonSellerIntelligenceOnePager'
    },

    layout: 'VideoReportStarterLayout'
  },

  {
    id: 'agentic-readiness-roadmap-tracker',
    title: 'Agentic Readiness Roadmap Tracker',
    route: '/agentic-readiness-roadmap-tracker',
    description: 'Assess a website\'s readiness for agentic commerce by analyzing its technical infrastructure, API capabilities, UX patterns, and AI integration potential.',
    category: 'Content',
    icon: 'report',
    about: 'Asses a website\'s readiness for agentic commerce',
    builtWith: ['Amazon Quick Flows', 'Amazon Quick Apps'],

    // Same gradient hero band + tilted video treatment as Dynamic Pricing
    // and RFP Response Assistant (see UseCaseView / VideoEmbed's `chrome`
    // mode).
    heroBanner: true,

    components: {
      // Recorded walkthrough of the flow + app (since Quick Flow/App
      // can't be live-embedded today -- see AWS community confirmation:
      // "quick apps cannot be embedded into external websites"). Moved off
      // the local MP4 (public/videos/AgenticReadinessDemoVid.mp4, no
      // longer referenced) onto a YouTube embed -- VideoEmbed's
      // isLocalVideoFile check only matches local media file extensions,
      // so a youtube.com/embed URL like this one automatically falls
      // through to its <iframe> branch instead of the native <video> one.
      // Query params trim the pre-play chrome as much as YouTube allows:
      // modestbranding hides the YouTube logo, rel=0 stops it suggesting
      // unrelated videos, iv_load_policy=3 disables info-card annotations.
      // YouTube doesn't expose a param to remove the title/channel text
      // overlaid on the thumbnail before play -- that's not configurable.
      video: {
        url: 'https://www.youtube.com/embed/Yr-iyJqKXwE?modestbranding=1&rel=0&iv_load_policy=3',
      },

      text: {
        heading: 'See it in action',
        body: 'A walkthrough of the assessor scoring a live storefront across API, AI, and UX & accessibility readiness, then generating a phased roadmap to close the gaps.'
      },

      // The one-pager's actual text, rebuilt as real responsive HTML (see
      // AgenticReadinessOnePager.jsx) instead of the raw embedded PDF -- the
      // PDF (still at the CloudFront URl below, kept here for reference)
      // was used as the source material for this component's content, same
      // pattern as DynamicPricingOnePager. A flat PDF/image just shrinks on
      // mobile until its text is unreadable; this reflows and stays legible
      // at any screen width.
      // Source PDF: https://d3cn5bx9ltufhv.cloudfront.net/reports/Agentic%20Commerce%20Readiness%20Assessor%20demo%20output.pdf
      richContent: 'AgenticReadinessOnePager'
    },

    layout: 'VideoReportStarterLayout'
  },

  {
    id: 'dynamic-pricing-promotion-intelligence',
    title: 'Dynamic Pricing & Promotion Intelligence',
    route: '/dynamic-pricing-promotion-intelligence',
    description: 'An automated pricing workflow, built with Amazon Quick Flows and Quick Apps, that watches competitor pricing and recommends action before the promotional window closes.',
    category: 'Pricing & Promotions',
    icon: 'report',
    about: 'Automates competitive pricing analysis and promotion decisions using Amazon Quick Flows and Quick Apps, with a human reviewer approving every recommendation.',
    builtWith: ['Amazon Quick Flows', 'Amazon Quick Apps'],

    // Shows the title + video together on a full-bleed gradient hero band
    // at the top of the page (see UseCaseView) instead of the plain white
    // header. DynamicPricingOnePager no longer renders its own title/video.
    heroBanner: true,

    components: {
      // Real Dynamic Pricing walkthrough, now a YouTube embed instead of
      // the local DynamicPricingPresenterDemoVid.mp4 file (no longer
      // referenced) -- VideoEmbed's isLocalVideoFile check only matches
      // local media file extensions, so this youtube.com/embed URL falls
      // through to its <iframe> branch automatically.
      video: {
        url: 'https://www.youtube.com/embed/PQSs8OfIcJ8?modestbranding=1&rel=0&iv_load_policy=3',
      },

      text: {
        heading: 'See it in action',
        body: 'A three-minute walkthrough of the pricing agent flagging an underperforming SKU, researching a competitor\'s live price, and routing a recommendation to a reviewer for approval.'
      },

      // The one-pager's actual text, rebuilt as real responsive HTML (see
      // DynamicPricingOnePager.jsx) instead of a flat image -- an image
      // just shrinks on mobile until its text is unreadable, but this
      // reflows and stays legible at any screen width.
      richContent: 'DynamicPricingOnePager'
    },

    layout: 'VideoReportStarterLayout'
  },

  {
    id: 'rfp-response-assistant',
    title: 'RFP Response Assistant',
    route: '/rfp-response-assistant',
    description: 'An agent that reads a customer\'s RFP price template and cost data, runs the pricing waterfall against your guardrails, and returns a submission-ready workbook for a human to review.',
    category: 'Sales & Proposals',
    icon: 'chat',
    about: 'Classifies the RFP template, SAP cost extract, and freight data in your bid workspace, applies your pricing guardrails, and fills the customer\'s exact submission template -- try it live below.',
    builtWith: ['Amazon Quick Suite'],

    // Same gradient hero band + tilted video treatment as Dynamic Pricing
    // (see UseCaseView / VideoEmbed's `chrome` mode).
    heroBanner: true,

    components: {
      // Walkthrough of "BidFlow": watched the video frame by frame to
      // write the richContent one-pager below (RFPResponseOnePager) -- it
      // classifies a customer's RFP price template plus SAP cost and
      // freight data, runs a pricing waterfall against configured
      // guardrails (increase cap, margin floor), and returns a
      // submission-ready Excel workbook (source tabs, pricing analysis,
      // executive summary, customer submission, assumptions log). The
      // original `text` copy here undersold that as generic proposal Q&A.
      // Now a YouTube embed instead of the local RFPUseCaseVid.mp4 file
      // (no longer referenced) -- VideoEmbed's isLocalVideoFile check only
      // matches local media file extensions, so this youtube.com/embed URL
      // falls through to its <iframe> branch automatically.
      video: {
        url: 'https://www.youtube.com/embed/Cq4_nSqWCN4?modestbranding=1&rel=0&iv_load_policy=3',
      },

      // The one-pager's actual text, rebuilt as real responsive HTML (see
      // RFPResponseOnePager.jsx) instead of a one-line text blurb -- same
      // pattern as DynamicPricingOnePager / AgenticReadinessOnePager. The
      // live chat agent below still lets a visitor try the real thing.
      richContent: 'RFPResponseOnePager',

      // QuickSight/Quick Suite chat agent ID for the real BidFlow agent.
      // This briefly failed to load ("Unable to load the chat agent.") --
      // root cause was the shared embed Lambda's SHARED_CHAT_USER_ARN
      // pointing at a QuickSight user (wls-retail-chat-reader) that no
      // longer existed in the account, which broke every agent's embed,
      // not just this one. That's fixed on the backend now (env var
      // repointed to an existing registered user), so this uses the real
      // agent ID again.
      // Tried pre-loading the bid-sheet prompt as the agent's first message
      // on open (an `initialPrompt` here, wired through ChatEmbed /
      // VideoChatStarterLayout) so a visitor would see the full BidFlow run
      // immediately. Backed out: the QuickSight chat embed SDK sends
      // `initialPrompt` to the agent regardless of the
      // showInitialPromptMessage flag, so it fired a full live run
      // (document classification, cost waterfall, Excel generation) on
      // every single page load even when the message bubble itself was
      // hidden -- worse than a blank input box, since it looked like
      // nothing was happening while a run was going in the background.
      // The same prompt is offered instead as plain suggested text in the
      // "Try asking" list next to the chat (see VideoChatStarterLayout),
      // which a visitor copies in themselves.
      chat: {
        agentId: '06aa3eb6-83ee-429e-a614-4abe5fa19f64',
      }
    },

    layout: 'VideoChatStarterLayout'
  },

  {
    id: 'predictive-restock-dynamic-offer-personalization',
    title: 'Predictive Restock & Dynamic Offer Personalization',
    route: '/predictive-restock-dynamic-offer-personalization',
    description: 'The convenience engine that turns the weekly shop into an ambient utility, predicting each household\'s consumption cycle and deploying a margin-optimized restock offer before they run out.',
    category: 'Merchandising & Inventory',
    icon: 'report',
    about: 'Predicts the exact day a household will run out of a product and delivers a margin-optimized, inventory-aware restock offer at their habitual planning window, before a competitor gets there first.',
    builtWith: ['Amazon Kinesis', 'Amazon SageMaker', 'Amazon EventBridge'],

    // Same gradient hero band as the other one-pager use cases (see
    // UseCaseView).
    heroBanner: true,

    components: {
      // Real Predictive Restock walkthrough, now a YouTube embed instead of
      // the local Restock_Video_AWS_Demo.mp4 file (no longer referenced, but
      // still on disk under app/public/videos/) -- VideoEmbed's
      // isLocalVideoFile check only matches local media file extensions, so
      // this youtube.com/embed URL falls through to its <iframe> branch
      // automatically, same as the other YouTube-embed use cases below.
      video: {
        url: 'https://www.youtube.com/embed/A8inhp8Y7bg?modestbranding=1&rel=0&iv_load_policy=3',
      },

      // The one-pager's actual text, rebuilt as real responsive HTML (see
      // PredictiveRestockOnePager.jsx) from the AWS one-pager PDF, same
      // pattern as DynamicPricingOnePager / RFPResponseOnePager.
      richContent: 'PredictiveRestockOnePager'
    },

    layout: 'VideoReportStarterLayout'
  },

  {
    id: 'personalized-content-product-discovery',
    title: 'Personalized Content & Product Discovery',
    route: '/personalized-content-product-discovery',
    description: 'Predictive, intent-driven discovery for retail & grocery, an agent that reasons about intent, diet, and budget instead of returning rigid keyword matches.',
    category: 'Search & Discovery',
    icon: 'report',
    about: 'Replaces rigid keyword search with an agent that reasons about intent, dietary rules, and budget, then plans and executes a personalized recipe bundle or filled cart in seconds.',
    builtWith: ['Amazon Kinesis', 'Amazon Personalize', 'Amazon DynamoDB', 'Amazon Bedrock'],

    // Same gradient hero band as the other one-pager use cases (see
    // UseCaseView). No recorded walkthrough yet -- heroImage below fills
    // the video's spot in the hero band with a static preview instead;
    // swap in a real `video: { url }` once a walkthrough exists (see
    // AgenticReadinessOnePager's entry above for that shape) and it takes
    // priority over heroImage automatically.
    heroBanner: true,

    components: {
      // Static mockup of a semantic search results screen: a
      // natural-language query bar ("taco night for 4") with filter chips
      // pulled from the query, plus a distinct purple chip showing a
      // dietary restriction applied automatically from the shopper's
      // profile even though it wasn't typed (and isn't in the query bar
      // either -- dropped "under $150" and the separate budget chip since
      // they read as redundant with the profile-driven framing). Each
      // GF-relevant result calls the restriction out too ("GF-safe --
      // your allergy profile") instead of a generic relevance score, and
      // a "Smart Swap" tag flags one allergy substitution. A
      // search/results treatment reads better for discovery than the
      // conversational-agent mockup used for Predictive Restock, since
      // this use case is about surfaced, ranked results, not a
      // back-and-forth chat. See HeroImagePreview in UseCaseView. (v3:
      // dropped the earlier product-feed concept and its per-card
      // match-percentage badges for this search-bar treatment, then
      // enlarged the sub-text and top filter chips and lightened their
      // color for legibility -- kept the filenames throughout so no
      // useCases.js path changes were needed, just the image content. A
      // follow-up pass sized everything up once more, but that read as
      // too large, so this is back to the prior sizing.)
      heroImage: {
        src: '/images/personalized-content-discovery-demo-preview.png',
        // Dedicated phone render (see HeroImagePreview) -- same search
        // content, redesigned as a 4:3 frame with a single 2x2 card grid
        // and larger text/icons, same approach used for the Predictive
        // Restock mobile image.
        mobileSrc: '/images/personalized-content-discovery-demo-preview-mobile.png',
        mobileAspectClassName: 'aspect-[4/3]',
        alt: 'A semantic search results screen for "taco night for 4, under $150", showing a gluten-free filter applied automatically from the shopper\'s profile, ranked product results, and a smart allergy substitution',
      },

      // The one-pager's actual text, rebuilt as real responsive HTML (see
      // PersonalizedContentDiscoveryOnePager.jsx) from the AWS one-pager
      // PDF, same pattern as DynamicPricingOnePager / RFPResponseOnePager.
      richContent: 'PersonalizedContentDiscoveryOnePager'
    },

    layout: 'VideoReportStarterLayout'
  },

  {
    id: 'personalized-search',
    title: 'Personalized Search',
    route: '/personalized-search',
    description: 'A live OpenSearch-powered storefront demo showing semantic and personalized product search in action.',
    category: 'Search & Discovery',
    icon: 'site',
    about: 'Embeds the West Loop Supplies demo storefront, built on Amazon OpenSearch Service, so visitors can try semantic and personalized product search directly instead of just reading about it.',
    builtWith: ['Amazon OpenSearch Service'],

    // No hero video for this one, so it keeps the plain white header
    // (heroBanner defaults to falsy/unset) instead of the gradient band.

    components: {
      text: {
        heading: 'Try it below',
        body: 'Search for something like "coffee" and notice the results go beyond exact keyword matches -- that\'s the semantic/personalized search at work.'
      },

      // Moved off of retail.westloop.io onto its own subdomain so that
      // domain is free to be repointed at this new site later (see
      // punch-list item 5) without breaking this embed. The CloudFront
      // distribution behind this domain also had its response headers
      // policy swapped from the AWS managed security-headers policy (which
      // sets X-Frame-Options: SAMEORIGIN, blocking all embedding) to a
      // custom policy that keeps the same protections but allows framing
      // from this app's domain via a CSP frame-ancestors directive instead.
      site: {
        url: 'https://personalizedsearch.westloop.io',
        height: '80vh'
      }
    },

    layout: 'SiteStarterLayout'
  }
];

// Removed: the "Agentic Readiness Roadmap Tracker (Live Embed)" experiment.
// Confirmed dead end -- AWS's embedding API (generate_embed_url_for_registered_user)
// has no ExperienceConfiguration option for Quick Apps today (checked
// against current boto3 docs), and the GenerativeQnA/InitialTopicId
// workaround the backend Lambda tried instead returned a 500 when tested
// live. Not pursuing further; the recorded-video version above (the
// original "Agentic Readiness Roadmap Tracker" entry) is the real one.
